import React, { useState, useEffect } from 'react';
import {
    Modal, Button, Card, Badge, Spinner, Alert, Row, Col, 
    Dropdown, OverlayTrigger, Tooltip, ProgressBar, Table,
    Form, InputGroup
} from 'react-bootstrap';
import {
    FaPlus, FaTrash, FaStar, FaRegStar, FaInfoCircle, 
    FaCog, FaDatabase, FaHdd, FaClock, FaList
} from 'react-icons/fa';
import { cloudAPI } from '../../utils/httpClient';
import Select from 'react-select';
import { useI18n } from '../../i18n';

// Authentication helper function (consistent with CloudSpaceNew.js)
const getAuthHeaders = () => {
    try {
        const userInfoStr = localStorage.getItem('userInfo');
        if (!userInfoStr) {
            console.error("userInfo not found in localStorage");
            return null;
        }

        const userInfo = JSON.parse(userInfoStr);
        const userId = userInfo?.id || userInfo?.user_id;
        const orgCode = userInfo?.org_code;
        const userLevel = userInfo?.user_level;

        if (!userId) {
            console.error("User ID not found in parsed userInfo");
            return null;
        }

        const headers = {
            'X-User-Id': userId.toString(),
        };
        if (orgCode) {
            headers['X-Org-Code'] = orgCode;
        }
        if (userLevel !== undefined && userLevel !== null) {
            headers['X-User-Level'] = userLevel.toString();
        }
        return headers;
    } catch (error) {
        console.error("Error parsing userInfo from localStorage:", error);
        return null;
    }
};

const VectorizationCombinationManager = ({ file, show, onHide, onUpdate }) => {
    const { t } = useI18n();
    const [combinations, setCombinations] = useState({});
    const [defaultCombination, setDefaultCombination] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedCombo, setSelectedCombo] = useState(null);
    const [comboDetails, setComboDetails] = useState(null);
    const [totalCombinations, setTotalCombinations] = useState(0);
    
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Add combination form state
    const [newComboLoading, setNewComboLoading] = useState(false);
    const [sourceColumn, setSourceColumn] = useState('');
    const [targetColumns, setTargetColumns] = useState([]);
    const [formError, setFormError] = useState(null);
    
    const showError = (title, message) => {
        setError({ title, message });
        setTimeout(() => setError(null), 5000);
    };
    
    const showSuccess = (message) => {
        setSuccess(message);
        setTimeout(() => setSuccess(null), 3000);
    };

    // Load combinations data
    useEffect(() => {
        if (show && file) {
            loadCombinations();
        }
    }, [show, file]);

    const loadCombinations = async () => {
        if (!file) return;
        
        setLoading(true);
        try {
            const response = await cloudAPI.getVectorizationCombinations(file.id);
            if (response.success) {
                const data = response.data;
                setCombinations(data.combinations || {});
                setDefaultCombination(data.default_combination);
                setTotalCombinations(data.total_combinations || 0);
            } else {
                showError(t('cloud:combination.failedLoad'), response.message);
            }
        } catch (error) {
            console.error('Error loading combinations:', error);
            showError(t('cloud:combination.failedLoad'), error.response?.data?.message || error.message);
        } finally {
            setLoading(false);
        }
    };

    const setAsDefault = async (comboId) => {
        try {
            const response = await cloudAPI.setDefaultVectorizationCombination(file.id, comboId);
            if (response.success) {
                showSuccess(t('cloud:combination.setDefaultSuccess'));
                setDefaultCombination(comboId);
                if (onUpdate) onUpdate();
            } else {
                showError(t('cloud:combination.failedSetDefault'), response.message);
            }
        } catch (error) {
            console.error('Error setting default combination:', error);
            showError(t('cloud:combination.failedSetDefault'), error.response?.data?.message || error.message);
        }
    };

    const deleteCombination = async (comboId, comboName) => {
        if (!window.confirm(t('cloud:combination.deleteConfirm', { name: comboName }))) {
            return;
        }

        try {
            const response = await cloudAPI.deleteVectorizationCombination(file.id, comboId);
            if (response.success) {
                const freedStorage = response.data?.freed_storage_mb || 0;
                showSuccess(t('cloud:combination.deleteSuccess', { mb: freedStorage.toFixed(2) }));
                loadCombinations(); // Reload data
                if (onUpdate) onUpdate();
            } else {
                showError(t('cloud:combination.failedDelete'), response.message);
            }
        } catch (error) {
            console.error('Error deleting combination:', error);
            showError(t('cloud:combination.failedDelete'), error.response?.data?.message || error.message);
        }
    };

    const viewDetails = async (comboId) => {
        setSelectedCombo(comboId);
        setComboDetails(null);
        setShowDetailsModal(true);

        try {
            const response = await cloudAPI.getVectorizationCombinationDetails(file.id, comboId);
            if (response.success) {
                setComboDetails(response.data);
            } else {
                showError(t('cloud:combination.failedDetails'), response.message);
            }
        } catch (error) {
            console.error('Error getting combination details:', error);
            showError(t('cloud:combination.failedDetails'), error.response?.data?.message || error.message);
        }
    };

    const handleAddCombination = async () => {
        if (!sourceColumn) {
            setFormError(t('cloud:cloudSpace.errors.selectSource'));
            return;
        }
        if (!targetColumns || targetColumns.length === 0) {
            setFormError(t('cloud:combination.targetHint'));
            return;
        }

        setNewComboLoading(true);
        setFormError(null);

        try {
            const response = await cloudAPI.createVectorizationCombination(file.id, sourceColumn, targetColumns);
            if (response.success) {
                showSuccess(t('cloud:combination.newStarted'));
                setShowAddModal(false);
                setSourceColumn('');
                setTargetColumns([]);
                
                // Close the combination manager and notify parent about new task
                if (onUpdate) {
                    onUpdate({
                        type: 'new_combination_started',
                        taskId: response.data?.task_id,
                        combinationId: response.data?.combination_id,
                        fileId: file.id
                    });
                }
                
                // Close the manager after starting new combination
                setTimeout(() => {
                    onHide();
                }, 1000);
            } else {
                setFormError(response.data.message || t('cloud:combination.failedCreate'));
            }
        } catch (error) {
            console.error('Error creating combination:', error);
            setFormError(error.response?.data?.message || error.message || t('cloud:combination.failedCreate'));
        } finally {
            setNewComboLoading(false);
        }
    };

    const formatFileSize = (sizeInMB) => {
        if (sizeInMB < 0.01) return '< 0.01 MB';
        if (sizeInMB < 1) return `${sizeInMB.toFixed(2)} MB`;
        if (sizeInMB < 1024) return `${sizeInMB.toFixed(1)} MB`;
        return `${(sizeInMB / 1024).toFixed(2)} GB`;
    };

    const renderCombinationCard = (comboId, combo) => {
        const isDefault = defaultCombination === comboId;
        
        return (
            <Card key={comboId} className={`mb-3 ${isDefault ? 'border-primary' : ''}`}>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <strong className="me-2">
                            {combo.name || `${combo.source_column} → ${combo.target_columns?.join(', ')}`}
                        </strong>
                        {isDefault && (
                            <Badge bg="primary" className="me-2">
                                <FaStar className="me-1" />
                                {t('cloud:combination.default')}
                            </Badge>
                        )}
                        <small className="text-muted">
                            ID: {comboId}
                        </small>
                    </div>
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-${comboId}`}>
                            <FaCog />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {!isDefault && (
                                <Dropdown.Item onClick={() => setAsDefault(comboId)}>
                                    <FaStar className="me-2" />
                                    {t('cloud:combination.setDefault')}
                                </Dropdown.Item>
                            )}
                            <Dropdown.Item onClick={() => viewDetails(comboId)}>
                                <FaInfoCircle className="me-2" />
                                {t('cloud:combination.viewDetails')}
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item 
                                onClick={() => deleteCombination(comboId, combo.name)} 
                                className="text-danger"
                            >
                                <FaTrash className="me-2" />
                                {t('cloud:combination.deleteCombination')}
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <small className="text-muted">{t('cloud:cloudSpace.labels.sourceColumn')}:</small><br />
                            <Badge bg="info">{combo.source_column}</Badge>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted">{t('cloud:cloudSpace.labels.targetColumns')}:</small><br />
                            {combo.target_columns?.map(col => (
                                <Badge key={col} bg="secondary" className="me-1">
                                    {col}
                                </Badge>
                            ))}
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col md={4}>
                            <small className="text-muted d-flex align-items-center">
                                <FaDatabase className="me-1" />
                                {t('cloud:combination.card.nodes')}: {combo.nodes?.toLocaleString() || '0'}
                            </small>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted d-flex align-items-center">
                                <FaHdd className="me-1" />
                                {t('cloud:combination.card.storage')}: {formatFileSize(combo.storage_size_mb || 0)}
                            </small>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted d-flex align-items-center">
                                <FaClock className="me-1" />
                                {t('cloud:combination.card.created')}: {combo.created_at ? new Date(combo.created_at).toLocaleDateString() : t('cloud:combination.card.unknown')}
                            </small>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    const renderDetailsModal = () => {
        return (
            <Modal show={showDetailsModal} onHide={() => {}} size="lg" backdrop="static" keyboard={false}>
                <Modal.Header>
                    <Modal.Title>{t('cloud:combination.detailsTitle', { id: selectedCombo })}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!comboDetails ? (
                        <div className="text-center">
                            <Spinner animation="border" />
                            <p className="mt-2">{t('cloud:combination.loadingDetails')}</p>
                        </div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>{t('cloud:combination.basicInfo')}</strong>
                                    <Table size="sm" className="mt-2">
                                        <tbody>
                                            <tr>
                                                <td>{t('cloud:combination.fields.id')}</td>
                                                <td>{comboDetails.id}</td>
                                            </tr>
                                            <tr>
                                                <td>{t('cloud:combination.fields.isDefault')}</td>
                                                <td>
                                                    {comboDetails.is_default ? (
                                                        <Badge bg="primary">{t('cloud:combination.yes')}</Badge>
                                                    ) : (
                                                        <Badge bg="secondary">{t('cloud:combination.no')}</Badge>
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{t('cloud:cloudSpace.labels.sourceColumn')}</td>
                                                <td><Badge bg="info">{comboDetails.source_column}</Badge></td>
                                            </tr>
                                            <tr>
                                                <td>{t('cloud:cloudSpace.labels.targetColumns')}</td>
                                                <td>
                                                    {comboDetails.target_columns?.map(col => (
                                                        <Badge key={col} bg="secondary" className="me-1">
                                                            {col}
                                                        </Badge>
                                                    ))}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{t('cloud:combination.fields.nodeCount')}</td>
                                                <td>{comboDetails.nodes?.toLocaleString() || '0'}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                                <Col md={6}>
                                    <strong>Storage Information</strong>
                                    <Table size="sm" className="mt-2">
                                        <tbody>
                                            <tr>
                                                <td>Total Size</td>
                                                <td>{formatFileSize(comboDetails.storage_info?.total_size_mb || 0)}</td>
                                            </tr>
                                            <tr>
                                                <td>File Count</td>
                                                <td>{comboDetails.storage_info?.files?.length || 0}</td>
                                            </tr>
                                            <tr>
                                                <td>S3 Path</td>
                                                <td>
                                                    <small className="text-muted">
                                                        {comboDetails.index_path}
                                                    </small>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>

                            {comboDetails.storage_info?.files && comboDetails.storage_info.files.length > 0 && (
                                <>
                                    <strong>{t('cloud:combination.storageFileList')}</strong>
                                    <Table striped hover size="sm" className="mt-2">
                                        <thead>
                                            <tr>
                                                <th>{t('cloud:combination.fileName')}</th>
                                                <th>{t('cloud:combination.size')}</th>
                                                <th>{t('cloud:combination.lastModified')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {comboDetails.storage_info.files.map((file, index) => (
                                                <tr key={index}>
                                                    <td><small>{file.name}</small></td>
                                                    <td><small>{formatFileSize(file.size_mb)}</small></td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {file.last_modified ? 
                                                                new Date(file.last_modified).toLocaleString() : 
                                                                t('cloud:combination.card.unknown')
                                                            }
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
                        {t('cloud:combination.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const renderAddCombinationModal = () => {
        const columnOptions = file?.columns?.map(col => ({ value: col, label: col })) || [];
        const targetColumnOptions = columnOptions.filter(opt => opt.value !== sourceColumn);

        return (
            <Modal show={showAddModal} onHide={() => {}} size="lg" backdrop="static" keyboard={false}>
                <Modal.Header>
                    <Modal.Title>Add New Vectorization Combination</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && <Alert variant="danger">{formError}</Alert>}
                    
                    <Form.Group className="mb-3">
                        <Form.Label><strong>{t('cloud:combination.sourceSelectPlaceholder').replace('-- ', '').replace(' --','')}</strong></Form.Label>
                        <Form.Select 
                            value={sourceColumn}
                            onChange={(e) => {
                                setSourceColumn(e.target.value);
                                setTargetColumns(prev => prev.filter(col => col !== e.target.value)); 
                                setFormError(null);
                            }}
                            disabled={newComboLoading || !file?.columns || file.columns.length === 0}
                        >
                            <option value="" disabled>-- {t('cloud:combination.sourceSelectPlaceholder')} --</option>
                            {columnOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">{t('cloud:combination.sourceHint')}</Form.Text>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                        <Form.Label><strong>{t('cloud:combination.targetLabel')}</strong></Form.Label>
                        <Select
                            isMulti
                            options={targetColumnOptions}
                            value={targetColumns.map(col => ({ value: col, label: col }))}
                            onChange={(selected) => {
                                setTargetColumns(selected ? selected.map(item => item.value) : []);
                                setFormError(null);
                            }}
                            isDisabled={newComboLoading || !sourceColumn || targetColumnOptions.length === 0}
                            placeholder={t('cloud:combination.targetPlaceholder')}
                        />
                        <Form.Text className="text-muted">{t('cloud:combination.targetHint')}</Form.Text>
                    </Form.Group>

                    {!file?.columns || file.columns.length === 0 && (
                        <Alert variant="warning">
                            <FaInfoCircle className="me-2" />
                            {t('cloud:combination.noColumnsInfo')}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={newComboLoading}>
                        {t('cloud:combination.close')}
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleAddCombination} 
                        disabled={newComboLoading || !sourceColumn || !targetColumns.length}
                    >
                        {newComboLoading ? (<><Spinner size="sm" className="me-1" /> {t('cloud:combination.creating')}</>) : (t('cloud:combination.create'))}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    return (
        <>
            <Modal show={show} onHide={() => {}} size="xl" backdrop="static" keyboard={false} dialogClassName="elevated-modal">
                <Modal.Header>
                    <Modal.Title className="d-flex align-items-center">
                        <FaList className="me-2" />
                        {t('cloud:combination.manageTitle', { name: file?.name })}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{maxHeight: '70vh', overflowY: 'auto'}}>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            <strong>{error.title || t('cloud:combination.error')}</strong>: {error.message}
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    )}
                    {loading ? (
                        <div className="text-center">
                            <Spinner animation="border" />
                            <p className="mt-2">{t('cloud:combination.loadingInfo')}</p>
                        </div>
                    ) : (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h6 className="mb-1">
                                        {t('cloud:combination.currentCombinations', { count: totalCombinations })}
                                    </h6>
                                    <small className="text-muted">
                                        {t('cloud:combination.fileHash')}: {file?.file_hash?.substring(0, 16)}...
                                    </small>
                                </div>
                                <Button 
                                    variant="primary" 
                                    size="sm" 
                                    onClick={() => setShowAddModal(true)}
                                >
                                    <FaPlus className="me-1" />
                                    {t('cloud:combination.addNew')}
                                </Button>
                            </div>
                            
                            {totalCombinations === 0 ? (
                                <Alert variant="info">
                                    <FaInfoCircle className="me-2" />
                                    {t('cloud:combination.emptyNote')}
                                </Alert>
                            ) : (
                                <>
                                    {/* Combination Statistics */}
                                    <Row className="mb-3">
                                        <Col md={4}>
                                            <Card className="text-center">
                                                <Card.Body className="py-2">
                                                    <h5 className="mb-1">{totalCombinations}</h5>
                                                    <small className="text-muted">{t('cloud:combination.stats.total')}</small>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card className="text-center">
                                                <Card.Body className="py-2">
                                                    <h5 className="mb-1">
                                                        {Object.values(combinations).reduce((sum, combo) => 
                                                            sum + (combo.storage_size_mb || 0), 0
                                                        ).toFixed(1)} MB
                                                    </h5>
                                                    <small className="text-muted">{t('cloud:combination.stats.totalSize')}</small>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card className="text-center">
                                                <Card.Body className="py-2">
                                                    <h5 className="mb-1">
                                                        {Object.values(combinations).reduce((sum, combo) => 
                                                            sum + (combo.nodes || 0), 0
                                                        ).toLocaleString()}
                                                    </h5>
                                                    <small className="text-muted">{t('cloud:combination.stats.totalNodes')}</small>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Combination List */}
                                    {Object.entries(combinations).map(([comboId, combo]) => 
                                        renderCombinationCard(comboId, combo)
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        {t('cloud:combination.close')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Details Modal */}
            {renderDetailsModal()}

            {/* Add Combination Modal */}
            {renderAddCombinationModal()}
        </>
    );
};

export default VectorizationCombinationManager;
